import { useEffect, useMemo, useState, MouseEvent } from "react";
import {
  Menu,
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import Breadcrumbs from "./Breadcrumbs";
import Section from "./Section";

import ArrowDropDownCircleIcon from "@mui/icons-material/ArrowDropDownCircle";

import { useData } from "../contexts/DataContext";

import { DashboardInfo } from "../utils/types";
import { getSectionDashboardsForMenu } from "../utils/findDashboardById";
import DefaultDuration from "./DefaultDuration";
import { employeeDashboardOrder } from "../utils/consts";

export interface IDashboardHeaderProps {
  readonly dashboardInfo: DashboardInfo | null;
  readonly hideBreadcrumbs?: boolean;
  readonly hideSectionMenu?: boolean;
  readonly showTimeline?: string;
}

export default function DashboardHeader({
  dashboardInfo,
  hideBreadcrumbs = false,
  hideSectionMenu = false,
  showTimeline,
}: IDashboardHeaderProps) {
  const theme = useTheme();

  const { dashboardList } = useData();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const sectionDashboards = useMemo(() => {
    if (!dashboardInfo?.section) {
      return [];
    }
    return getSectionDashboardsForMenu(dashboardList, dashboardInfo.section);
  }, [dashboardInfo?.section, dashboardList]);

  useEffect(() => {
    handleMenuClose();
  }, [dashboardInfo]);

  const getDashboardTitle = () => {
    if (dashboardInfo?.title) {
      return dashboardInfo.title;
    }
    return "Loading...";
  };

  if (dashboardInfo?.section === "Employee Dashboard") {
    sectionDashboards.sort((a: any, b: any) => {
      const orderA =
        employeeDashboardOrder[
          a.title as keyof typeof employeeDashboardOrder
        ] ?? 999;
      const orderB =
        employeeDashboardOrder[
          b.title as keyof typeof employeeDashboardOrder
        ] ?? 999;
      return orderA - orderB;
    });
  }

  return (
    <>
      <Box sx={{ px: 3 }}>
        {hideBreadcrumbs !== true && (
          <Breadcrumbs dashboardInfo={dashboardInfo} />
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          my={0}
          mx={0}
          p={0}
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            mt={1}
            mb={0}
            mx={0}
            p={0}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "24px",
                fontWeight: 500,
                color: theme.palette.text.primary,
              }}
            >
              {getDashboardTitle()}
            </Typography>

            {hideSectionMenu !== true && (
              <IconButton
                color="primary"
                aria-label="Section Dashboards"
                onClick={handleMenuOpen}
              >
                <ArrowDropDownCircleIcon />
              </IconButton>
            )}
          </Stack>
          {showTimeline && <DefaultDuration duration={showTimeline} />}
        </Stack>
      </Box>

      {hideSectionMenu !== true && (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          id={"dashboards-list"}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          open={isMenuOpen}
          onClose={handleMenuClose}
          sx={{
            borderRadius: "4px",
            ".MuiPaper-root": {
              width: "330px",
              borderRadius: "4px",
            },
            ".MuiMenu-list": {
              m: 0,
              p: 0,
              borderRadius: "4px",
            },
          }}
        >
          {dashboardInfo && (
            <Section
              dashboards={sectionDashboards}
              section={dashboardInfo.section}
            />
          )}
        </Menu>
      )}
    </>
  );
}
